import { useState, useEffect, useCallback } from 'react';
import { useAppDispatch } from '@/redux/hooks/useAppDispatch';
import { AppDispatch } from '@/redux/store';
import {
  getConfigurationOptionsWithSubgroup,
  updateConfiguration,
} from '@/redux/configuration/configuration.thunks';
import { buildConfigRoute, Route } from '@/constants/routes';
import { useToast } from '@/shared/shadcn/hooks/use-toast';
import { getError } from '@/utils/error-report/getError';
import { ConfigItem } from '@/components/admin/tables/ConfigsTable';

const GROUP_ORDER_INDEX = 0;
const SUBGROUP_ORDER_INDEX = 1;
const SUBGROUP_NOT_FOUND_INDEX = -1;
const DEFAULT_ORDER = 0;

interface UseConfigurationsProps {
  configTypes: string[];
}

interface SubgroupMap {
  title: string;
  route: Route;
  configTypes: string[];
  order?: number;
}

interface GroupMap {
  title: string;
  items: SubgroupMap[];
  order?: number;
}

interface NavigationConfig {
  title: string;
  items: SubgroupMap[];
}

const getGeneratedConfigs = async (dispatch: AppDispatch): Promise<NavigationConfig[]> => {
  const groups = new Map<string, GroupMap>();

  try {
    const configsData = await dispatch(getConfigurationOptionsWithSubgroup()).unwrap();

    if (!configsData || !Array.isArray(configsData)) {
      // No valid configuration data received
      return [];
    }

    configsData.forEach(parameter => {
      const order = parameter.parameterOrder.toString();
      const groupOrder = order[GROUP_ORDER_INDEX];
      const subgroupOrder = order[SUBGROUP_ORDER_INDEX];

      // If group doesn't exist, create new maps
      if (!groups.has(parameter.group)) {
        const subgroupMap: SubgroupMap = {
          title: parameter.subgroup,
          route: buildConfigRoute(parameter.subgroup),
          configTypes: [parameter.type],
          order: Number(subgroupOrder),
        };

        const groupMap: GroupMap = {
          title: parameter.group,
          items: [subgroupMap],
          order: Number(groupOrder),
        };

        groups.set(parameter.group, groupMap);
      } else {
        const groupMap = groups.get(parameter.group)!;

        // Check if subgroup exists
        const existingSubgroupIndex = groupMap.items.findIndex(
          item => item.title === parameter.subgroup,
        );
        if (existingSubgroupIndex !== SUBGROUP_NOT_FOUND_INDEX) {
          // Add type to existing subgroup
          groupMap.items[existingSubgroupIndex].configTypes.push(parameter.type);
        } else {
          // Create new subgroup
          const newSubgroup: SubgroupMap = {
            title: parameter.subgroup,
            route: buildConfigRoute(parameter.subgroup),
            configTypes: [parameter.type],
            order: Number(subgroupOrder),
          };

          groupMap.items.push(newSubgroup);
        }
      }
    });

    // Sort groups by order
    const sortedGroups = Array.from(groups.values()).sort((a, b) => {
      if (a.order !== undefined && b.order !== undefined) {
        return a.order - b.order;
      }
      return DEFAULT_ORDER;
    });

    // Sort subgroups by order
    sortedGroups.forEach(group => {
      group.items.sort((a, b) => {
        if (a.order !== undefined && b.order !== undefined) {
          return a.order - b.order;
        }
        return DEFAULT_ORDER;
      });
    });

    // Convert Map to array format expected by the app
    return Array.from(sortedGroups.values()).map(group => ({
      title: group.title,
      items: group.items,
    }));
  } catch (error) {
    // Failed to fetch configuration options
    return [];
  }
};

export const useConfigurations = ({
  configTypes,
}: UseConfigurationsProps): {
  configs: ConfigItem[];
  handleGetConfig: () => Promise<void>;
  handleUpdateConfig: (key: string, value: string) => Promise<void>;
  pageConfigs: NavigationConfig[];
} => {
  const dispatch = useAppDispatch();
  const { toast } = useToast();
  const [configs, setConfigs] = useState<ConfigItem[]>([]);
  const [pageConfigs, setPageConfigs] = useState<NavigationConfig[]>([]);

  useEffect(() => {
    const loadConfigs = async (): Promise<void> => {
      const generatedConfigs = await getGeneratedConfigs(dispatch);
      setPageConfigs(generatedConfigs);
    };
    loadConfigs();
  }, [dispatch]);

  const handleGetConfig = useCallback(async () => {
    try {
      const response = await dispatch(getConfigurationOptionsWithSubgroup()).unwrap();

      if (response) {
        const mappedConfigs = response
          .filter(configResponse => configTypes.includes(configResponse.type))
          .sort((a, b) => {
            const orderA = Number(a.parameterOrder) || DEFAULT_ORDER;
            const orderB = Number(b.parameterOrder) || DEFAULT_ORDER;
            return orderA - orderB;
          })
          .map(configResponse => ({
            id: configResponse.type,
            key: configResponse.commonName,
            value: configResponse.value || '',
            valueType: configResponse.valueType,
          }));

        setConfigs(mappedConfigs);
      }
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to fetch configurations',
        variant: 'destructive',
      });
    }
  }, [dispatch, configTypes, toast]);

  const handleUpdateConfig = useCallback(
    async (key: string, value: string) => {
      try {
        const configToUpdate = configs.find(config => config.key === key);
        if (!configToUpdate) {
          throw new Error('Configuration not found');
        }

        await dispatch(
          updateConfiguration({
            id: configToUpdate.id,
            value,
          }),
        ).unwrap();
        toast({
          title: 'Success',
          description: 'Configuration updated successfully',
          variant: 'default',
        });

        await handleGetConfig();
      } catch (error) {
        const serializedError = getError({
          error,
          prefix: 'Failed to update configuration',
        });
        toast({
          title: 'Error',
          description: serializedError.message || 'Failed to update configuration',
          variant: 'destructive',
        });
      }
    },
    [configs, dispatch, toast, handleGetConfig],
  );

  return {
    configs,
    handleGetConfig,
    handleUpdateConfig,
    pageConfigs,
  };
};
